import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { NetworkBackground } from "./NetworkBackground";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.14 },
  }),
};

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
    >
      {/* Particle network */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <NetworkBackground />
      </motion.div>

      {/* Radial vignette for depth */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(120% 90% at 50% 10%, rgba(5,5,5,0) 40%, rgba(5,5,5,0.85) 100%)",
        }}
      />

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-[100svh] flex flex-col justify-center pt-24 pb-16"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-xs uppercase tracking-[0.28em] text-[#00E5FF] font-bold mb-7 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-[#00E5FF]/60" />
          Managed IT · Cybersecurity · Cloud
        </motion.p>

        <h1 className="font-black tracking-tighter text-white text-5xl md:text-7xl lg:text-[6rem] leading-[0.92] max-w-[15ch]">
          {["Architecting", "the future", "of your IT."].map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
              >
                {i === 2 ? (
                  <span>
                    of your <span className="text-[#00E5FF] glow-cyan">IT.</span>
                  </span>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9 }}
          className="mt-8 max-w-xl text-[#A0AEC0] text-base md:text-lg leading-relaxed"
        >
          Leonard Technology Inc. is the managed service partner for companies
          that refuse downtime. We engineer resilient, secure networks that run
          quietly in the background — so your business never stops.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta"
            onClick={() => onNavigate?.("#contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-[#00E5FF] text-[#050505] px-7 py-4 font-semibold hover:bg-white transition-colors duration-300"
          >
            Book a discovery call
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </button>
          <button
            data-testid="hero-services-link"
            onClick={() => onNavigate?.("#services")}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-7 py-4 font-medium hover:border-white/40 hover:bg-white/5 transition-all duration-300"
          >
            Explore services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#5A6472]"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};
